import { useEffect, useState } from "react";
import { toast } from "sonner";
import { apiCreate, apiDelete, apiList, apiUpdate } from "@/lib/api";
import { defaultWhatsAppTemplates } from "@/lib/whatsapp";
import { useActiveClinicaId } from "@/hooks/useActiveClinicaId";
import { useAuth } from "@/contexts/AuthContext";

export interface WhatsappModelo {
  id: string;
  nome: string;
  tipo: string;
  mensagem: string;
  ativo: boolean;
  created_at?: string;
  updated_at?: string;
}

export type WhatsappModeloInput = Omit<WhatsappModelo, "id" | "created_at" | "updated_at">;

export function useWhatsappModelos() {
  const { user } = useAuth();
  const activeClinicaId = useActiveClinicaId();
  const [modelos, setModelos] = useState<WhatsappModelo[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchModelos = async () => {
    if (!user) return;
    try {
      setLoading(true);
      const rows = await apiList<WhatsappModelo>("whatsapp_modelos");
      setModelos(rows.sort((a, b) => a.nome.localeCompare(b.nome)));
    } catch (error: any) {
      toast.error("Erro ao carregar modelos: " + error.message);
      setModelos([]);
    } finally {
      setLoading(false);
    }
  };

  const createModelo = async (input: WhatsappModeloInput) => {
    try {
      const data = await apiCreate<WhatsappModelo>("whatsapp_modelos", input);
      await fetchModelos();
      toast.success("Modelo salvo!");
      return data;
    } catch (error: any) {
      toast.error("Erro ao salvar modelo: " + error.message);
      return null;
    }
  };

  const updateModelo = async (id: string, input: Partial<WhatsappModeloInput>) => {
    try {
      const data = await apiUpdate<WhatsappModelo>("whatsapp_modelos", id, input);
      await fetchModelos();
      toast.success("Modelo atualizado!");
      return data;
    } catch (error: any) {
      toast.error("Erro ao atualizar modelo: " + error.message);
      return null;
    }
  };

  const deleteModelo = async (id: string) => {
    try {
      await apiDelete("whatsapp_modelos", id);
      await fetchModelos();
      toast.success("Modelo removido!");
    } catch (error: any) {
      toast.error("Erro ao remover modelo: " + error.message);
    }
  };

  const seedDefaults = async () => {
    if (modelos.length > 0) return;
    for (const modelo of defaultWhatsAppTemplates) {
      await apiCreate<WhatsappModelo>("whatsapp_modelos", { ...modelo, ativo: true });
    }
    await fetchModelos();
    toast.success("Modelos padrão criados!");
  };

  useEffect(() => {
    fetchModelos();
  }, [user?.id, activeClinicaId]);

  return { modelos, loading, fetchModelos, createModelo, updateModelo, deleteModelo, seedDefaults };
}
